import React, { Component } from 'react';
import { isEqual } from 'lodash';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import memoize from 'fast-memoize';
import ExportableTable from 'containers/tables/ExportableTable';
import helpers from 'helpers';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import { insertBeforeItem, insertAfterItem } from 'helpers/utilities';
import { displayNameToKey } from 'helpers/channels';
import { generateFooterData, generateHeaders } from 'helpers/tables';

const DEFAULT_ACCESSORS = ['impressions', 'ecpa_activities', 'spend'];

const capitalizeChannels = (data) => {
    for (let i = 0; i < data.length; i += 1) {
        Object.keys(data[i]).forEach((key) => {
            if (key === 'channel') { data[i][key] = helpers.convertToDisplayValue(data[i][key]); }
        });
    }

    return data;
};

const createTableData = memoize((data, props) => {
    const modifiedData = capitalizeChannels(data);
    const headers = generateHeaders({
        keys: [
            'impressions', 'ecpa_activities', 'campaign_activities', 'total_activities',
            'clicks', 'ctr', 'conversion_revenue', 'spend', 'ecpa'
        ],
        footerData: generateFooterData({ data: modifiedData, filteredKeys: ['channel'] }),
        colOptions: {
            labelCode: props.entityObjectiveLabelCode
        }
    });

    let { accessors = DEFAULT_ACCESSORS } = props;

    if (props.showTotalActivities && !accessors.includes('total_activities')) {
        if (accessors.includes('campaign_activities')) {
            accessors = insertBeforeItem(accessors, 'campaign_activities', 'total_activities');
        } else {
            accessors = insertBeforeItem(accessors, 'ecpa_activities', 'total_activities');
        }
    }

    if (props.showeCPA && !accessors.includes('ecpa')) {
        accessors = insertAfterItem(accessors, 'spend', 'ecpa');
    }

    if (props.showConversionRevenue && !accessors.includes('conversion_revenue')) {
        accessors = insertAfterItem(accessors, 'spend', 'conversion_revenue');
    }

    const columns = accessors.map(accessor => headers[accessor]);

    return { data: modifiedData, columns };
});

class ChannelOverviewTable extends Component {
    constructor(props) {
        super(props);

        this.viewChannelLink = this.viewChannelLink.bind(this);

        const { data, columns } = createTableData(props.data, props);

        this.state = {
            data,
            columns,
            accessors: props.accessors || DEFAULT_ACCESSORS,
            showTotalActivities: props.showTotalActivities,
            showeCPA: props.showeCPA,
            title: props.title,
            entityId: props.entityId,
            exportName: props.exportName
        };
    }

    static capitalizeChannels(data) {
        for (let i = 0; i < data.length; i += 1) {
            Object.keys(data[i]).forEach((key) => {
                if (key === 'channel') { data[i][key] = helpers.convertToDisplayValue(data[i][key]); }
            });
        }

        return data;
    }

    viewChannelLink(row) {
        if (this.props.disableChannelLinks) {
            return <span>{helpers.convertToDisplayValue(row.value)}</span>;
        }
        return <Link to={`/dashboard/${this.state.entityId}/channel/${displayNameToKey(row.value)}`} title="Go to Channel">
            {helpers.convertToDisplayValue(row.value)}
        </Link>;
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps, this.props);
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const { data: nextData, columns: nextColumns } = createTableData(nextProps.data, nextProps);
        const nextEntityId = nextProps.entityId;
        const nextTitle = nextProps.title;
        const nextAccessors = nextProps.accessors;
        const nextExportName = nextProps.exportName;
        const nextShowTotalActivities = nextProps.showTotalActivities;
        const nextShowConversionRevenue = nextProps.showConversionRevenue;
        const nextEntityObjectiveLabelCode = nextProps.entityObjectiveLabelCode;
        const nextShoweCPA = nextProps.showeCPA;

        return {
            ...prevState,
            data: nextData,
            columns: nextColumns,
            entityId: nextEntityId,
            accessors: nextAccessors,
            showTotalActivities: nextShowTotalActivities,
            showeCPA: nextShoweCPA,
            showConversionRevenue: nextShowConversionRevenue,
            entityObjectiveLabelCode: nextEntityObjectiveLabelCode,
            title: nextTitle,
            exportName: nextExportName
        };
    }

    render() {
        const { data, columns, title, exportName } = this.state;

        return (
            <ContentTile padding={helpers.contentPadding('table')}>
                <ContentHeader tableData={data} exportName={exportName} type={'table'}>{title}</ContentHeader>

                <ExportableTable
                    key={1}
                    title={title}
                    exportName={exportName}
                    data={data}
                    columns={[
                        {
                            Header: 'CHANNEL',
                            accessor: 'channel',
                            Cell: row => <CellDiv value={this.viewChannelLink(row)} />,
                            Footer: (
                                <CellDiv value='Total' strong />
                            )
                        },
                        ...columns
                    ]}
                    getTfootProps={() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                    getTheadThProps={() => ({ style: { outline: 0 } })}
                    showPagination={false}
                    defaultSorted={[
                        {
                            id: 'channel',
                            desc: false
                        }
                    ]}
                    minRows={data.length < RowsPerPageDefault ? data.length : RowsPerPageDefault}
                    className="-highlight"
                />
            </ContentTile>
        );
    }
}

ChannelOverviewTable.propTypes = {
    entityId: PropTypes.number.isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
        channel: PropTypes.string.isRequired,
        campaign_activities: PropTypes.number.isRequired,
        impressions: PropTypes.number.isRequired,
        spend: PropTypes.number.isRequired,
    })).isRequired,
    accessors: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string.isRequired,
    showTotalActivities: PropTypes.bool,
    disableChannelLinks: PropTypes.bool,
    entityObjectiveLabelCode: PropTypes.string
};

export default ChannelOverviewTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/ChannelOverviewTable.js